import { instanceBo } from "../utils/axios.create";
import {
	PropagationParticipants,
	PropagationJob,
} from "../pages/TreatmentPlans/propagationStatus";
import {
	TreatmentPlan,
	TreatmentPlanRequest,
} from "../domain/treatmentPlan.interface";

const getTreatmentPlans = async (
	treatmentPlanId?: string,
): Promise<TreatmentPlan[]> => {
	const pathTreatmentPlanId = treatmentPlanId ? `/${treatmentPlanId}` : "";

	return instanceBo
		.get(`/treatmentplans${pathTreatmentPlanId}`)
		.then((res) => {
			// API returns single object when searching by ID, array otherwise
			const data = res.data.body;
			return Array.isArray(data) ? data : [data];
		})
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const getTreatmentPlan = async (
	treatmentPlanId: string,
): Promise<TreatmentPlan> => {
	return instanceBo
		.get(`/treatmentplans/${treatmentPlanId}`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const createTreatmentPlan = async (
	treatmentPlan: TreatmentPlanRequest,
): Promise<TreatmentPlan> => {
	return instanceBo
		.post("/treatmentplans", treatmentPlan)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const updateTreatmentPlan = async (
	treatmentPlanId: string,
	treatmentPlan: TreatmentPlanRequest,
): Promise<TreatmentPlan> => {
	return instanceBo
		.put(`/treatmentplans/${treatmentPlanId}`, treatmentPlan)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const deleteTreatmentPlan = async (treatmentPlanId: string): Promise<void> => {
	return instanceBo
		.delete(`/treatmentplans/${treatmentPlanId}`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

export interface GenerateSmartProgramPayload {
	description: string;
	preferredMode?: string;
}

export interface GenerateSmartProgramResponse {
	workflow: Record<string, unknown>;
	fieldDefinitions: Array<{
		fieldName: string;
		fieldLabel: string;
		fieldType: string;
		required: boolean;
		options?: string[];
		helpText?: string;
	}>;
	previewHtml: string;
	description: string;
	prompt: string;
}

/**
 * A single thing the validator found wrong with, or missing from, a generated
 * workflow (SIMS-8208).
 *
 * `severity` is the whole basis of the panel's behaviour: "Warnings don't block
 * activation. Errors do." `path` says which step it belongs to, which is how
 * the panel groups them — a finding with no path has nowhere to render.
 */
export interface WorkflowFinding {
	severity: "ERROR" | "WARN" | "INFO";
	code: string;
	message: string;
	path?: string;
	suggestion?: string;
}

export interface GenerationStatusResponse {
	jobId: string | null;
	status: "PENDING" | "IN_PROGRESS" | "SUCCESS" | "FAILED" | null;
	error: string | null;
	/**
	 * The structured findings behind a FAILED generation, when there are any.
	 * `error` remains the joined sentence; this is what the validation panel
	 * renders per step.
	 */
	findings?: WorkflowFinding[] | null;
	result: GenerateSmartProgramResponse | null;
}

const generateSmartProgram = async (
	treatmentPlanId: string,
	payload: GenerateSmartProgramPayload,
): Promise<{ jobId: string }> => {
	return instanceBo
		.post(`/treatmentplans/${treatmentPlanId}/generate-smart-program`, payload)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const getGenerationStatus = async (
	treatmentPlanId: string,
): Promise<GenerationStatusResponse> => {
	return instanceBo
		.get(`/treatmentplans/${treatmentPlanId}/generate-smart-program/status`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

export type ChatRole = "USER" | "ASSISTANT";

export type ChatSessionStatus =
	| "AWAITING_INPUT"
	| "IN_PROGRESS"
	| "READY_TO_GENERATE"
	| "FAILED";

export interface ChatMessage {
	role: ChatRole;
	content: string;
	ts: string;
}

export interface SmartProgramChatSession {
	_id: string;
	treatmentPlanId: string;
	accountId: string;
	messages: ChatMessage[];
	draft: Record<string, unknown> | null;
	status: ChatSessionStatus;
	bedrockSessionId: string;
	lastTurnError?: string;
	lastTurnStartedAt?: string;
	createdAt: string;
	updatedAt?: string;
}

export interface ChatTurnPayload {
	sessionId?: string;
	message: string;
}

const startSmartProgramChatTurn = async (
	treatmentPlanId: string,
	payload: ChatTurnPayload,
): Promise<{ sessionId: string }> => {
	return instanceBo
		.post(`/treatmentplans/${treatmentPlanId}/smart-program/chat-turn`, payload)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const getSmartProgramChatSession = async (
	treatmentPlanId: string,
	sessionId: string,
): Promise<SmartProgramChatSession> => {
	return instanceBo
		.get(
			`/treatmentplans/${treatmentPlanId}/smart-program/chat-session/${sessionId}`,
		)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

/**
 * Structurally the same session as Smart Program's, and deliberately a
 * separate type: `draft` means something different. Here it is the treatment
 * config being designed, which the client turns into a template — there is no
 * second generation step.
 */
export type TreatmentConfigChatSession = SmartProgramChatSession;

const startTreatmentConfigChatTurn = async (
	treatmentPlanId: string,
	payload: ChatTurnPayload,
): Promise<{ sessionId: string }> => {
	return instanceBo
		.post(
			`/treatmentplans/${treatmentPlanId}/treatment-config/chat-turn`,
			payload,
		)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

const getTreatmentConfigChatSession = async (
	treatmentPlanId: string,
	sessionId: string,
): Promise<TreatmentConfigChatSession> => {
	return instanceBo
		.get(
			`/treatmentplans/${treatmentPlanId}/treatment-config/chat-session/${sessionId}`,
		)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

export interface RequiredFieldsResponse {
	programType: string;
	fieldDefinitions: Array<{
		fieldName: string;
		fieldLabel: string;
		fieldType: string;
		required: boolean;
		options?: string[];
		defaultValue?: string;
		helpText?: string;
	}>;
}

const getRequiredFields = async (
	treatmentPlanId: string,
): Promise<RequiredFieldsResponse> => {
	return instanceBo
		.get(`/treatmentplans/${treatmentPlanId}/required-fields`)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response.data);
		});
};

/**
 * SIMS-9031 — ask what a threshold change WOULD do, before doing it.
 *
 * A read: it never writes an enrollment. The counts it returns are what the administrator has to
 * acknowledge, and the same numbers are recomputed server-side at confirm time so a stale
 * acknowledgment is refused (AC7).
 */
const previewThresholdPropagation = async (
	treatmentPlanId: string,
	body: {
		templateName: string;
		/** SIMS-9041: the template's stable id — matched in addition to the name, never instead. */
		templateId?: string;
		nextThreshold: number;
		/**
		 * FR-11: the preview MUST be told which field is moving. Without it the backend counts the
		 * whole cohort for a segment change, promising more participants than apply() can reach.
		 */
		field?: "TEMPLATE_DEFAULT" | "SEGMENT";
		segmentWeekRange?: { start: number; end: number };
		templateThresholdBeforeSave?: number;
		includeCustomised?: boolean;
	},
): Promise<{
	toUpdate: number;
	customised: number;
	forecastDisenrollments: number;
	forecastDisenrolledIds: string[];
	/** SIMS-9032 AC1 — the participants behind the figure, for the inspectable list. */
	forecastDisenrolled: {
		enrollmentId: string;
		beneficiaryId?: string;
		daysAbsent: number;
		currentSegment?: { start: number; end: number };
		currentThreshold: number;
		newThreshold: number;
	}[];
	propagationInProgress: boolean;
}> => {
	return instanceBo
		.post(
			`/treatmentplans/${treatmentPlanId}/threshold-propagation/preview`,
			body,
		)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data ?? err);
		});
};

/**
 * SIMS-9028 — apply the change the administrator acknowledged.
 *
 * Returns 202 with a job id: the save is accepted before the cohort is finished, because the
 * lambda's API Gateway integration caps the caller at 29 seconds (NFR-1). A 409 means either a
 * propagation is already running for this plan (SIMS-9035) or the forecast moved since it was
 * shown (AC7) — the body carries the recomputed figures in the latter case.
 */
const applyThresholdPropagation = async (
	treatmentPlanId: string,
	body: {
		templateName: string;
		/** SIMS-9041: the template's stable id — matched in addition to the name, never instead. */
		templateId?: string;
		nextThreshold: number;
		acknowledgedDisenrollments: number;
		/**
		 * SIMS-9036 AC1 / CR-1: the administrator making the change. Required — it is recorded on
		 * the change record and on every participant's history entry, and a change that can end an
		 * enrollment has to be attributable to a real person.
		 */
		actorId: string;
		/** FR-8: a SEGMENT change writes into that phase of each snapshot, not the template default. */
		field?: "TEMPLATE_DEFAULT" | "SEGMENT";
		segmentWeekRange?: { start: number; end: number };
		templateThresholdBeforeSave?: number;
		includeCustomised?: boolean;
		/**
		 * FR-19: the plan's propagation option as THIS save sets it. AC6 writes the plan only after
		 * this call returns, so the persisted flag is still false on a first-time opt-in — without
		 * this the server refuses the change the administrator just acknowledged.
		 */
		propagateThresholdToCurrentEnrollments?: boolean;
	},
): Promise<{ jobId: string; status: string }> => {
	return instanceBo
		.post(`/treatmentplans/${treatmentPlanId}/threshold-propagation`, body)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data ?? err);
		});
};

/**
 * SIMS-9033 (AC2/AC3/AC4) — the most recent propagation for a plan, running or finished.
 *
 * Read from the persisted Job rather than anything held in the page, so the result survives
 * navigating away and is still there on another day. Returns null when the plan has never had one.
 */
const getThresholdPropagationStatus = async (
	treatmentPlanId: string,
): Promise<PropagationJob | null> => {
	return instanceBo
		.get(`/treatmentplans/${treatmentPlanId}/threshold-propagation/status`)
		.then((res) => res.data.body ?? null)
		.catch((err) => {
			return Promise.reject(err.response?.data ?? err);
		});
};

/**
 * SIMS-9037 — every threshold change applied to this plan, newest first.
 *
 * Distinct from the status read above, which answers "what is happening now" from the latest job.
 * An empty array is a real answer (AC5: "there is nothing to show"), not an error — so a plan with
 * no propagations must be distinguishable from a request that failed.
 */
const getThresholdPropagationHistory = async (
	treatmentPlanId: string,
): Promise<PropagationJob[]> => {
	return instanceBo
		.get(`/treatmentplans/${treatmentPlanId}/threshold-propagation/history`)
		.then((res) => res.data.body ?? [])
		.catch((err) => {
			return Promise.reject(err.response?.data ?? err);
		});
};

/**
 * SIMS-9038 — who was in this change's cohort, and what happened to each.
 *
 * Requested per ENTRY, only when one is expanded: the history list deliberately carries no
 * participant ids, so a single response can never hold several cohorts. Ids only, no names (PHI) —
 * PO conditions, SIMS-9024 comments 66185 and 66232.
 */
const getThresholdPropagationParticipants = async (
	treatmentPlanId: string,
	jobId: string,
): Promise<PropagationParticipants | null> => {
	return instanceBo
		.get(
			`/treatmentplans/${treatmentPlanId}/threshold-propagation/entry/${jobId}/participants`,
		)
		.then((res) => res.data.body ?? null)
		.catch((err) => {
			return Promise.reject(err.response?.data ?? err);
		});
};

/**
 * SIMS-9035 AC5 — the plans that currently have a threshold change being applied.
 *
 * ONE call for the whole list. A status request per card would mean 60-80 requests to draw the
 * treatment-plans screen, which is what the bulk endpoint exists to avoid.
 */
const getRunningThresholdPropagations = async (): Promise<string[]> => {
	return instanceBo
		.get("/treatmentplans/threshold-propagation/running")
		.then((res) => res.data.body?.treatmentPlanIds ?? [])
		.catch(() => {
			// The list must render regardless: a missing indicator is a smaller failure than a
			// blank screen, and the save-time refusal still protects the cohort.
			return [];
		});
};

/**
 * SIMS-9034 — retry the participants a threshold change could not reach.
 *
 * Safe to repeat (AC3): a participant already holding the new value is written nothing, so no
 * duplicate history entry is added.
 */
const retryThresholdPropagation = async (
	treatmentPlanId: string,
	jobId: string,
): Promise<{ status: string; processed: number; failed: number }> => {
	return instanceBo
		.post(
			`/treatmentplans/${treatmentPlanId}/threshold-propagation/${jobId}/retry`,
		)
		.then((res) => res.data.body)
		.catch((err) => {
			return Promise.reject(err.response?.data ?? err);
		});
};

export const treatmentPlanService = {
	getTreatmentPlans,
	getTreatmentPlan,
	createTreatmentPlan,
	updateTreatmentPlan,
	deleteTreatmentPlan,
	generateSmartProgram,
	getGenerationStatus,
	getRequiredFields,
	startSmartProgramChatTurn,
	getSmartProgramChatSession,
	startTreatmentConfigChatTurn,
	getTreatmentConfigChatSession,
	previewThresholdPropagation,
	applyThresholdPropagation,
	getThresholdPropagationStatus,
	getThresholdPropagationHistory,
	getThresholdPropagationParticipants,
	getRunningThresholdPropagations,
	retryThresholdPropagation,
};
